import { jsPDF } from "jspdf";

export type CardData = {
  name: string;
  title: string;
  company: string;
  phone: string;
  address: string;
  email: string;
};

// Company and address are the same for every scorer on the team - fixed,
// not editable, so nobody has to remember or mistype the office address.
export const FIXED_COMPANY = "Sportradar";
export const FIXED_ADDRESS = "Wiśniowa 1, 44-144 Żernica, Poland";

// This is a screen-review upload for a visa portal, not a print job, so the
// resolution is picked from the 300 KB cap rather than a fixed print DPI.
// At 300dpi (1004x650px) fitting under the cap forced JPEG quality down far
// enough to visibly blur the text. At 150dpi it cleared the cap at high
// quality with a lot of headroom to spare (~58 KB of 300 KB), so the target
// is raised to 240dpi - sharper text and gradient, still comfortably inside
// the cap even after the quality-search loop below runs.
export const TARGET_DPI = 240;
const MM_TO_PX = TARGET_DPI / 25.4;
export const CARD_W = Math.round(85 * MM_TO_PX);
export const CARD_H = Math.round(55 * MM_TO_PX);
export const MIN_BYTES = 10 * 1024;
export const MAX_BYTES = 300 * 1024;
// The portal's cap applies to the delivered PDF, not the JPEG embedded in it.
// A PDF wrapper adds a few KB of container overhead, so the image itself is
// size-searched against a slightly lower ceiling to leave that room.
const JPEG_MAX_BYTES = MAX_BYTES - 6 * 1024;

export function isSportradar(company: string): boolean {
  return company.trim().toLowerCase() === "sportradar";
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  if (!text) return [];
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const test = line ? line + " " + word : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Draws "sp[o]rt" (regular weight, the o replaced by a rounded-square
// outline with a small filled accent square inside) + "radar" (bold) -
// reproducing the real Sportradar lockup's weight contrast + o-mark.
function drawSportradarWordmark(ctx: CanvasRenderingContext2D, x: number, y: number, fontPx: number, inkColor: string, accentColor: string): number {
  let cursor = x;
  ctx.textAlign = "left";
  ctx.fillStyle = inkColor;
  ctx.font = `500 ${fontPx}px 'Space Grotesk', sans-serif`;
  ctx.fillText("sp", cursor, y);
  cursor += ctx.measureText("sp").width;

  const oSize = fontPx * 0.78;
  const oGap = fontPx * 0.045;
  const oTop = y - fontPx * 0.72;
  cursor += oGap;
  ctx.lineWidth = fontPx * 0.1;
  ctx.strokeStyle = accentColor;
  roundRect(ctx, cursor, oTop, oSize, oSize, oSize * 0.28);
  ctx.stroke();
  const dotSize = oSize * 0.44;
  ctx.fillStyle = accentColor;
  roundRect(ctx, cursor + (oSize - dotSize) / 2, oTop + (oSize - dotSize) / 2, dotSize, dotSize, dotSize * 0.32);
  ctx.fill();
  cursor += oSize + oGap;

  ctx.fillStyle = inkColor;
  ctx.font = `500 ${fontPx}px 'Space Grotesk', sans-serif`;
  ctx.fillText("rt", cursor, y);
  cursor += ctx.measureText("rt").width;

  ctx.font = `700 ${fontPx}px 'Space Grotesk', sans-serif`;
  ctx.fillText("radar", cursor, y);
  cursor += ctx.measureText("radar").width;

  return cursor - x;
}

function measureSportradarWordmark(ctx: CanvasRenderingContext2D, fontPx: number): number {
  ctx.font = `500 ${fontPx}px 'Space Grotesk', sans-serif`;
  const spW = ctx.measureText("sp").width;
  const rtW = ctx.measureText("rt").width;
  ctx.font = `700 ${fontPx}px 'Space Grotesk', sans-serif`;
  const radarW = ctx.measureText("radar").width;
  const oSize = fontPx * 0.78;
  const oGap = fontPx * 0.045;
  return spW + oGap + oSize + oGap + rtW + radarW;
}

async function ensureFontsReady(): Promise<void> {
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // ignore
    }
  }
}

export async function drawCard(data: CardData): Promise<HTMLCanvasElement> {
  await ensureFontsReady();
  const canvas = document.createElement("canvas");
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas 2D context unavailable.");
  const ctx = context;

  const ink = "#ffffff";
  const muted = "#c3c9ea";
  const accent = "#e2001a";

  // Narrower dark range than before (was #04061a at the edges) so text
  // never sits on a near-black patch with too little contrast against
  // the muted color.
  const bgGrad = ctx.createRadialGradient(CARD_W * 0.5, CARD_H * 0.42, 0, CARD_W * 0.5, CARD_H * 0.42, CARD_W * 0.65);
  bgGrad.addColorStop(0, "#14225e");
  bgGrad.addColorStop(0.62, "#0a1338");
  bgGrad.addColorStop(1, "#080f2c");
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, CARD_W, CARD_H);
  ctx.strokeStyle = "rgba(255, 255, 255, .1)";
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, CARD_W - 1, CARD_H - 1);

  const padX = CARD_W * 0.09;
  const padRight = CARD_W * 0.09;
  const contentW = CARD_W - padX - padRight;
  ctx.textBaseline = "alphabetic";

  let y = CARD_H * 0.27;
  ctx.fillStyle = ink;
  ctx.font = `700 ${Math.round(CARD_H * 0.086)}px 'Space Grotesk', sans-serif`;
  const nameLines = wrapLines(ctx, data.name, contentW);
  const nameLineHeight = CARD_H * 0.1;
  for (const line of nameLines) {
    ctx.fillText(line, padX, y);
    y += nameLineHeight;
  }

  if (data.company) {
    ctx.save();
    ctx.globalAlpha = 0.94;
    ctx.fillStyle = ink;
    ctx.font = `700 ${Math.round(CARD_H * 0.075)}px 'Space Grotesk', sans-serif`;
    const companyLines = wrapLines(ctx, data.company, contentW);
    for (const line of companyLines) {
      ctx.fillText(line, padX, y);
      y += CARD_H * 0.088;
    }
    ctx.restore();
  }

  if (data.title) {
    y += CARD_H * 0.02;
    ctx.fillStyle = muted;
    ctx.font = `500 ${Math.round(CARD_H * 0.05)}px 'Inter', sans-serif`;
    ctx.fillText(data.title, padX, y);
  }

  if (data.company) {
    ctx.save();
    const markFontPx = Math.round(CARD_H * 0.052);
    const markY = CARD_H * 0.155;
    if (isSportradar(data.company)) {
      const w = measureSportradarWordmark(ctx, markFontPx);
      drawSportradarWordmark(ctx, CARD_W - padRight - w, markY, markFontPx, ink, accent);
    } else {
      ctx.fillStyle = ink;
      ctx.font = `600 ${markFontPx}px 'Inter', sans-serif`;
      ctx.textAlign = "right";
      ctx.fillText(data.company, CARD_W - padRight, markY);
      ctx.textAlign = "left";
    }
    ctx.restore();
  }

  const rowStep = CARD_H * 0.072;
  const addrLines = wrapLines(ctx, data.address, contentW);
  const contactRows = 2 + Math.max(1, addrLines.length || 1);
  let contactY = CARD_H - CARD_H * 0.09 - rowStep * (contactRows - 1);

  // Centered in the gap between the title block and the contact rows,
  // rather than hugging the bottom - keeps it visually "between" them.
  const dividerY = y + (contactY - y) * 0.5;
  const dividerW = Math.max(76, CARD_W * 0.46);
  const dividerH = Math.max(6, CARD_H * 0.012);
  ctx.fillStyle = accent;
  roundRect(ctx, padX, dividerY - dividerH, dividerW, dividerH, dividerH / 2);
  ctx.fill();

  function contactRow(label: string, value: string): void {
    ctx.fillStyle = ink;
    ctx.font = `600 ${Math.round(CARD_H * 0.043)}px 'Inter', sans-serif`;
    ctx.fillText(label, padX, contactY);
    const labelW = ctx.measureText(label).width + CARD_H * 0.03;
    ctx.fillStyle = muted;
    ctx.font = `400 ${Math.round(CARD_H * 0.043)}px 'Inter', sans-serif`;
    ctx.fillText(value, padX + labelW, contactY);
    contactY += rowStep;
  }

  contactRow("T", data.phone || "—");
  contactRow("E", data.email || "—");
  ctx.fillStyle = muted;
  ctx.font = `400 ${Math.round(CARD_H * 0.043)}px 'Inter', sans-serif`;
  for (const line of addrLines.length ? addrLines : ["—"]) {
    ctx.fillText(line, padX, contactY);
    contactY += rowStep;
  }

  return canvas;
}

// JPEG's quality knob lets us hit a target byte ceiling reliably (unlike
// PNG) - search downward from a high starting quality until the blob fits.
async function blobWithinSizeLimit(canvas: HTMLCanvasElement, maxBytes: number): Promise<Blob | null> {
  const toJpeg = (quality: number) => new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  let quality = 0.94;
  let blob = await toJpeg(quality);
  while (blob && blob.size > maxBytes && quality > 0.3) {
    quality = Math.max(0.3, quality - 0.08);
    blob = await toJpeg(quality);
  }
  return blob;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export function safeCardFileName(name: string): string {
  return (name || "wizytowka").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "sportradar";
}

// Single source of truth for the exported PDF - both the public /wizytowka
// page's download button and the internal Discord-export page call this
// same function, so the bot posts byte-identical files to what a person
// gets by filling in the form themselves.
export async function generateCardPdf(data: CardData): Promise<Blob> {
  const canvas = await drawCard(data);
  const imageBlob = await blobWithinSizeLimit(canvas, JPEG_MAX_BYTES);
  if (!imageBlob) throw new Error("Nie udało się wygenerować obrazu.");
  const imageDataUrl = await blobToDataUrl(imageBlob);

  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: [85, 55] });
  pdf.addImage(imageDataUrl, "JPEG", 0, 0, 85, 55);
  const blob = pdf.output("blob") as Blob;
  if (blob.size > MAX_BYTES) {
    throw new Error("Plik wciąż przekracza 300 KB — zmniejsz ilość tekstu i spróbuj ponownie.");
  }
  return blob;
}
